import { Link, useParams } from "react-router-dom";

import SkillMarkdown from "@/components/SkillMarkdown";
import { BLOGS, getBlog } from "@/lib/blogs";
import { useSeoMeta } from "@/lib/useSeoMeta";
import NotFound from "./NotFound";

// The title lives in the masthead, so drop a single leading markdown H1 from the body.
function stripLeadingH1(md: string): string {
  return md.replace(/^\s*#\s+.*(?:\r?\n)+/, "");
}

export default function BlogPage() {
  const blogSlug = String(useParams().blogSlug);
  const blog = getBlog(blogSlug);
  useSeoMeta({
    title: blog ? `${blog.title} — SkillPatch` : "Docs — SkillPatch",
    description: blog?.description,
    path: `/docs/${blogSlug}`,
  });

  if (!blog) return <NotFound />;

  const index = BLOGS.findIndex((b) => b.slug === blog.slug);
  const next = BLOGS[index + 1];

  return (
    <main className="mx-auto max-w-[1300px] px-10 py-12">
      <header>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
          Agent Skills
        </p>
        <h1 className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-[3.25rem]">
          {blog.title}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-text/60">{blog.description}</p>
        <div className="mt-7 flex items-center gap-3 text-xs text-text/45">
          <span className="h-[3px] w-11 rounded-full bg-gradient-to-r from-accent to-primary" />
          <span className="font-mono uppercase tracking-wide">{blog.readMinutes} min read</span>
        </div>
      </header>

      <article className="mt-10">
        <SkillMarkdown md={stripLeadingH1(blog.content)} />
      </article>

      {next && (
        <Link
          to={`/docs/${next.slug}`}
          className="group mt-14 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition hover:-translate-y-0.5 hover:border-primary/50 hover:bg-white/[0.04]"
        >
          <div className="min-w-0">
            <p className="font-mono text-xs uppercase tracking-wide text-text/40">Next doc</p>
            <p className="mt-1.5 truncate text-lg font-semibold transition group-hover:text-primary">{next.title}</p>
          </div>
          <svg className="shrink-0 text-accent" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </Link>
      )}
    </main>
  );
}
